import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { documentCategories } from "../data/documentCategoriesData";
import UploadDocumentsCard from "../components/UploadDocumentsCard";
import AISummaryCard from "../components/AISummaryCard";
import DocumentsListCard from "../components/DocumentsListCard";
import { aiSummaryData } from "../data/aiSummaryData";
import PageHeader from "../../../common/PageHeader";
import "../styles/DueDiligence.css";
import { fetchCompanies, fetchDocumentsByCompanyId } from "../services/api";
import { navigationOptions } from "../../../common/navigation-bars/navigationOptions";

const DataRoom = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [activeCategory, setActiveCategory] = useState("all");
  const [docs, setDocs] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch company name and documents in parallel
        const [companies, documents] = await Promise.all([
          fetchCompanies(),
          fetchDocumentsByCompanyId(companyId),
        ]);

        const company = companies.find((c) => c.id === companyId);
        if (company) setCompanyName(company.name);

        // Transform API data to match expected format
        const transformedDocs = documents.map((doc) => ({
          id: doc.documentId,
          name: doc.documentName,
          category: doc.category,
          status: doc.processingStatus,
          companyId: doc.companyId,
          // Add any additional fields that might be needed
        }));

        setDocs(transformedDocs);
      } catch (err) {
        console.error("Failed to fetch data", err);
        setError("Failed to load documents. Please try again.");
        // Fallback to empty array on error
        setDocs([]);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const handleNavigation = (option) => {
    if (option.navigateTo) {
      navigate(option.navigateTo);
    }
    console.log("Navigate to:", option.value);
  };

  const filteredDocs =
    activeCategory === "all"
      ? docs
      : docs.filter(
          (doc) => doc.category.toLowerCase() === activeCategory.toLowerCase()
        );

  const heading =
    activeCategory === "all"
      ? "All Documents"
      : `${
          activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)
        } Documents`;

  const getCategoryCount = (categoryId) => {
    if (categoryId === "all") return docs.length;
    const categoryLabel = documentCategories.find(
      (cat) => cat.id === categoryId
    )?.label;
    return docs.filter((doc) => doc.category === categoryLabel).length;
  };

  const handleUpload = (newFiles) => {
    setDocs((prevDocs) => [...newFiles, ...prevDocs]);
  };

  if (loading) {
    return (
      <Box p={2}>
        <PageHeader
          title="Data Room - Loading..."
          description="Loading documents..."
          selectOptions={navigationOptions}
          onSelectChange={handleNavigation}
        />
        {/* <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Loading documents...</Typography>
        </Box> */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "150px",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <PageHeader
          title={`Data Room - ${companyName || "Error"}`}
          description="Error loading documents"
          selectOptions={navigationOptions}
          onSelectChange={handleNavigation}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography color="error">{error}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <PageHeader
        title={`Data Room - ${companyName || "Loading..."}`}
        description="All due diligence documents and files related to this company will appear here."
        selectOptions={navigationOptions}
        onSelectChange={handleNavigation}
      />

      {/* Row 2 - Document Categories Heading */}
      <Box mt={4} mb={2}>
        <Typography className="sub-header">Document Categories</Typography>
      </Box>

      {/* Row 3 - Button Tabs (Grid based) */}
      <Grid container spacing={1}>
        {documentCategories.map((cat) => (
          <Grid item key={cat.id}>
            <Box
              onClick={() => setActiveCategory(cat.id)}
              sx={{
                fontSize: "14px",
                textTransform: "none",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "6px 14px",
                backgroundColor:
                  activeCategory === cat.id ? "#1F61C5" : "#ffffff",
                color: activeCategory === cat.id ? "#ffffff" : "#0A1A27",
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    activeCategory === cat.id ? "#164a9f" : "#f5f5f5",
                },
              }}
            >
              {cat.label} ({getCategoryCount(cat.id)})
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box mt={4}>
        <Grid container spacing={2}>
          {/* Left column: 4/12 */}
          <Grid item size={{ md: 4 }}>
            <Box mb={3}>
              <UploadDocumentsCard
                onUpload={handleUpload}
                activeCategory={activeCategory}
                disabled={true}
              />
            </Box>
            <AISummaryCard aiSummaryData={aiSummaryData} />
          </Grid>

          {/* Right column: 8/12 */}
          <Grid item size={{ md: 8 }}>
            <DocumentsListCard documents={filteredDocs} heading={heading} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DataRoom;
